import React, { useEffect, useState, useCallback } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  Pagination,
  IconButton,
} from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import AllPhotosApi from "../../api_calls/AllPhotosApi";
import TogglePhotoFlaggedApi from "../../api_calls/TogglePhotoFlaggedApi";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import Configs from "../../utils/Configs";

const PHOTOS_PER_PAGE = 12;
const BASE_URL = Configs.baseUrl;

const PhotoCard = ({ photo, onFlag }) => (
  <Card
    sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      borderRadius: 2,
      boxShadow: 2,
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        position: "relative",
        paddingTop: "66.66%", // Maintain 3:2 aspect ratio
        overflow: "hidden",
      }}
    >
      <Zoom>
        <img
          src={`${BASE_URL}${photo.p_file_data}`}
          alt={`${photo.first_name} ${photo.other_names}`}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            cursor: "zoom-in",
          }}
        />
      </Zoom>
    </Box>

    <CardContent
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 1,
        px: 2,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flexGrow: 1,
        }}
      >
        {photo.first_name} {photo.other_names}
      </Typography>
      <IconButton
        size="small"
        aria-label="Flag photo"
        onClick={() => onFlag(photo.p_id)}
      >
        <FlagIcon
          fontSize="small"
          sx={{ color: photo.p_flagged === "yes" ? "red" : "inherit" }}
        />
      </IconButton>
    </CardContent>
  </Card>
);

const PhotoGallery = () => {
  const [photos, setPhotos] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchPhotos = useCallback(async (pageNumber) => {
    setLoading(true);
    try {
      const response = await AllPhotosApi.fetchPhotos(
        pageNumber,
        PHOTOS_PER_PAGE
      );

      if (response.status === "success" && Array.isArray(response.data)) {
        /**
         * FIX: Deduplication
         * We create a Map using p_id as the key. This ensures that even if
         * the API returns duplicates, the UI only stores unique photos.
         */
        const uniquePhotos = Array.from(
          new Map(response.data.map((item) => [item.p_id, item])).values()
        );

        setPhotos(uniquePhotos);
        setTotalPages(response.total_pages || 1);
      } else {
        setPhotos([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("[fetchPhotos] Error fetching photos:", error);
      setPhotos([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPhotos(page);
  }, [page, fetchPhotos]);

  const handlePageChange = (_, newPage) => {
    if (newPage !== page) {
      setPage(newPage);
      // Scroll to top when page changes
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleFlagClick = async (pId) => {
    try {
      const result = await TogglePhotoFlaggedApi.togglePhotoFlagged(pId);
      const { data, status } = result || {};
      if (status === "success" && data?.p_id) {
        setPhotos((prevPhotos) =>
          prevPhotos.map((photo) =>
            photo.p_id === data.p_id
              ? { ...photo, p_flagged: data.p_flagged }
              : photo
          )
        );
      }
    } catch (err) {
      console.error("[handleFlagClick] Exception:", err);
    }
  };

  return (
    <Box sx={{ p: 0 }}>
      <Typography
        fontWeight={600}
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}
      >
        <PhotoLibraryIcon fontSize="small" />
        All Uploaded Photos
      </Typography>

      <Grid container spacing={2}>
        {photos.map((photo) => (
          <Grid
            item
            xs={6}
            sm={4}
            md={2}
            // Use only p_id for the key to help React's diffing algorithm
            key={photo.p_id}
          >
            <PhotoCard photo={photo} onFlag={handleFlagClick} />
          </Grid>
        ))}
      </Grid>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress size={30} />
        </Box>
      )}

      {!loading && photos.length === 0 && (
        <Typography
          sx={{ textAlign: "center", mt: 4, color: "text.secondary" }}
        >
          No photos found.
        </Typography>
      )}

      {!loading && totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 6,
            pb: 4,
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}
    </Box>
  );
};

export default PhotoGallery;
